import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { LanguageSettings } from "@/components/sidebar/settings/LanguageSettings";
import { ColorModeSettings } from "@/components/sidebar/settings/ColorModeSettings";

import { AIFeatureSettings } from "@/components/sidebar/settings/AIFeatureSettings";
import { DevModeSettings } from "@/components/sidebar/settings/DevModeSettings";
import { DateTimeFormatSettings } from "@/components/sidebar/settings/DateTimeFormatSettings";
import { SettingsSectionWrapper } from "@/components/sidebar/settings/SettingsSectionWrapper";
import { GenerateEmbeddingsButton } from "@/components/admin/GenerateEmbeddingsButton";
import { DatabaseDiagnostics } from "@/components/admin/DatabaseDiagnostics";
import { IntegrationsTab } from "@/components/settings/IntegrationsTab";
import { EmailSettingsTab } from "@/components/settings/EmailSettingsTab";
import { DocumentUploadTab } from "@/components/settings/DocumentUploadTab";
import { ProfileForm } from "@/components/settings/ProfileForm";
import { ResetPasswordForm } from "@/components/settings/ResetPasswordForm";
import { NotificationPreferences } from "@/components/settings/NotificationPreferences";
import { useSettings } from "@/providers/SettingsProvider";
import { useAuth } from "@/providers/AuthProvider";
import { useTranslation } from "react-i18next";

import { Layout, Settings as SettingsIcon, Globe, Moon, Clock, Save, User, Key, Code, FileText, Bell, GripVertical, Mail } from "lucide-react";

const Settings = () => {	const { devMode, saveSettings, isSaving, hasUnsavedChanges, sidebarOrder, setSidebarOrderValue } = useSettings();
	const { isAdmin, isSuperAdmin } = useAuth();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const [activeTab, setActiveTab] = useState("general");
	const { t } = useTranslation();

	// Define available tabs dynamically
	const availableTabs = [
		{
			value: "general",
			icon: Layout,
			label: t("settings.tabs.general"),
			visible: true
		},
		{
			value: "notifications",
			icon: Bell,
			label: t("settings.tabs.notifications"),
			visible: true
		},
		{
			value: "ai",
			icon: SettingsIcon,
			label: t("settings.tabs.ai"),
			visible: isSuperAdmin
		},
		{
			value: "email",
			icon: Mail,
			label: t("settings.tabs.email", "Email"),
			visible: true
		},
		{
			value: "integrations",
			icon: Globe,
			label: t("settings.tabs.integrations"),
			visible: true
		},
		{
			value: "documentUpload",
			icon: FileText,
			label: t("settings.tabs.documentUpload"),
			visible: true
		},
		{
			value: "developer",
			icon: Code,
			label: t("settings.tabs.developer"),
			visible: isSuperAdmin
		}
	].filter(tab => tab.visible);

	const tabCount = availableTabs.length;

	usePageTitle("Settings");

	useEffect(() => {
		// Check if URL contains "code" parameter and set the tab to "integrations"
		const codeParam = searchParams.get("code");
		if (codeParam) {
			//console.log("Code param found: " + codeParam);
			setActiveTab("integrations");
		}

		// Check if URL contains "tab" parameter and set the active tab
		const tabParam = searchParams.get("tab");
		if (tabParam) {
			//console.log("Tab param found: " + tabParam);
			setActiveTab(tabParam);
		}

		const origin = searchParams.get("origin");
		if (origin) {
			//console.log("origin param found: " + origin);
			setActiveTab(origin);
		}


	}, [searchParams, location, activeTab, isAdmin]);

	return (
		<>
			<PageHeader
				title={t("settings.title")}
				description={t("settings.description")}
				actions={
					hasUnsavedChanges ? (
						<Button
							onClick={saveSettings}
							disabled={isSaving}
						>
							{isSaving ? t("settings.saving") : t("settings.saveSettings")}
							<Save className="ml-2 h-4 w-4" />
						</Button>
					) : null
				}
			/>			<Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
				
                <TabsList className="grid gap-2 w-full max-w-6xl mb-6" style={{ gridTemplateColumns: `repeat(${tabCount}, 1fr)` }}>
					{availableTabs.map((tab) => {
						const IconComponent = tab.icon;
						return (
							<TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
								<IconComponent size={16} />
								<span>{tab.label}</span>
							</TabsTrigger>
						);
					})}
				</TabsList>

				<TabsContent value="general" className="space-y-6">

					<Card>
						<CardHeader className="pb-3">
							<CardTitle className="text-xl flex items-center">
								<User className="mr-2 h-5 w-5" />
								{t("settings.userSettings.title")}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-8">

							<SettingsSectionWrapper title={
								<span className="flex items-center text-lg gap-2">
									<User className="h-4 w-4" />
									{t("settings.userSettings.profile.title")}
								</span>
							}>
								<div className="mt-3">
									<ProfileForm />
								</div>
								<div className="mt-3">
									<ResetPasswordForm />
								</div>
							</SettingsSectionWrapper>

						</CardContent>

					</Card>

					<Card>
						<CardHeader className="pb-3">
							<CardTitle className="text-xl flex items-center">
								<Layout className="mr-2 h-5 w-5" />
								{t("settings.displaySettings.title")}
							</CardTitle>
							<CardDescription>
								{t("settings.displaySettings.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-8">

							<SettingsSectionWrapper title={
								<span className="flex items-center text-lg gap-2">
									<Globe className="h-4 w-4" />
									{t("settings.displaySettings.language.title")}
								</span>
							}>
								<div className="mt-2">
									<LanguageSettings />
								</div>
							</SettingsSectionWrapper>

							<SettingsSectionWrapper title={
								<span className="flex items-center text-lg gap-2">
									<Moon className="h-4 w-4" />
									{t("settings.displaySettings.colorMode.title")}
								</span>
							}>
								<div className="mt-2">
									<ColorModeSettings />
								</div>
							</SettingsSectionWrapper>

							<SettingsSectionWrapper title={
								<span className="flex items-center text-lg gap-2">
									<Clock className="h-4 w-4" />
									{t("settings.displaySettings.dateTime.title")}
								</span>
							}>
								<div className="mt-3">
									<DateTimeFormatSettings />
								</div>
							</SettingsSectionWrapper>

							<SettingsSectionWrapper title={
								<span className="flex items-center text-lg gap-2">
									<GripVertical className="h-4 w-4" />
									{t("settings.displaySettings.sidebarOrder.title", "Sidebar Order")}
								</span>
							}>
								<div className="mt-2">
									<p className="text-sm text-muted-foreground mb-3">
										{t("settings.displaySettings.sidebarOrder.description", "Drag items in the sidebar to reorder them. Use the button below to reset to the default order.")}
									</p>
									<Button
										variant="outline"
										onClick={() => setSidebarOrderValue(null)}
										disabled={!sidebarOrder}
									>
										{t("navigation.resetOrder", "Reset order")}
									</Button>
								</div>
							</SettingsSectionWrapper>
						</CardContent>
						<CardFooter className="border-t pt-4">
							{hasUnsavedChanges && (
								<Button
									onClick={saveSettings}
									disabled={isSaving}
									className="ml-auto"
								>
									{isSaving ? t("settings.saving") : t("settings.saveSettings")}
									<Save className="ml-2 h-4 w-4" />
								</Button>
							)}
						</CardFooter>
					</Card>
				</TabsContent>

				<TabsContent value="notifications" className="space-y-6">
					<NotificationPreferences />
				</TabsContent>

				{isSuperAdmin && (
					<TabsContent value="ai" className="space-y-6">
						<Card>
							<CardHeader className="pb-3">
								<CardTitle className="text-xl flex items-center">
									<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
										<path d="M12 2c1.7 0 3.3.2 4.9.7 1.6.5 3 1.2 4.3 2.1 1.3.9 2.4 2 3.2 3.2.9 1.3 1.6 2.7 2.1 4.3.5 1.6.7 3.2.7 4.9s-.2 3.3-.7 4.9c-.5 1.6-1.2 3-2.1 4.3-.9 1.3-2 2.4-3.2 3.2-1.3.9-2.7 1.6-4.3 2.1-1.6.5-3.2.7-4.9.7s-3.3-.2-4.9-.7c-1.6-.5-3-1.2-4.3-2.1-1.3-.9-2.4-2-3.2-3.2-.9-1.3-1.6-2.7-2.1-4.3-.5-1.6-.7-3.2-.7-4.9s.2-3.3.7-4.9c.5-1.6 1.2-3 2.1-4.3.9-1.3 2-2.4 3.2-3.2 1.3-.9 2.7-1.6 4.3-2.1 1.6-.5 3.2-.7 4.9-.7z"></path>
										<circle cx="12" cy="12" r="3"></circle>
									</svg>
									{t("settings.aiSettings.title")}
								</CardTitle>
								<CardDescription>
									{t("settings.aiSettings.description")}
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-8">
								<SettingsSectionWrapper title={t("settings.aiSettings.featuresToggle")}>
									<div className="mt-2">
										<AIFeatureSettings />
									</div>
								</SettingsSectionWrapper>

								<SettingsSectionWrapper title={t("settings.aiSettings.vectorEmbeddings")}>
									<div className="mt-2">
										<p className="text-sm text-muted-foreground mb-4">
											{t("settings.aiSettings.vectorDescription")}
										</p>
										<div className="flex flex-wrap gap-3">
											<GenerateEmbeddingsButton entityType="candidates" />
											<GenerateEmbeddingsButton entityType="jobs" />
											<GenerateEmbeddingsButton entityType="companies" />
										</div>
									</div>
								</SettingsSectionWrapper>
							</CardContent>
							<CardFooter className="border-t pt-4">
								{hasUnsavedChanges && (
									<Button
										onClick={saveSettings}
										disabled={isSaving}
										className="ml-auto"
									>
										{isSaving ? t("settings.saving") : t("settings.saveSettings")}
										<Save className="ml-2 h-4 w-4" />
									</Button>
								)}
							</CardFooter>
						</Card>
					</TabsContent>

				)}				<TabsContent value="email" className="space-y-6">
					<EmailSettingsTab />
				</TabsContent>

				<TabsContent value="integrations" className="space-y-6">
					<IntegrationsTab />
				</TabsContent>

				<TabsContent value="documentUpload" className="space-y-6">
					<DocumentUploadTab />
				</TabsContent>

				{isSuperAdmin && (
					<TabsContent value="developer" className="space-y-6">
						<Card>
							<CardHeader className="pb-3">
								<CardTitle className="text-xl flex items-center">
									<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
										<polyline points="16 18 22 12 16 6"></polyline>
										<polyline points="8 6 2 12 8 18"></polyline>
									</svg>
									{t("settings.developer.title")}
								</CardTitle>
								<CardDescription>
									{t("settings.developer.description")}
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-8">
								<SettingsSectionWrapper title={t("settings.developer.devMode")}>
									<div className="mt-2">
										<DevModeSettings />
									</div>
								</SettingsSectionWrapper>

								{devMode && (
									<SettingsSectionWrapper title={t("settings.developer.database.title")}>
										<div className="mt-2">
											<p className="text-sm text-muted-foreground mb-4">
												{t("settings.developer.database.description")}
											</p>
											<DatabaseDiagnostics />
										</div>
									</SettingsSectionWrapper>
								)}
							</CardContent>
							<CardFooter className="border-t pt-4">
								{hasUnsavedChanges && (
									<Button
										onClick={saveSettings}
										disabled={isSaving}
										className="ml-auto"
									>
										{isSaving ? t("settings.saving") : t("settings.saveSettings")}
										<Save className="ml-2 h-4 w-4" />
									</Button>
								)}
							</CardFooter>
						</Card>
					</TabsContent>
				)}
			</Tabs>
		</>
	);
};

export default Settings;
